import { useState } from 'react'

export const AGENT_PROMPT =
  'Read https://opendeploy.dev/start and install OpenDeploy for this agent, then ask me whether to deploy the current project.'

export const INSTALL_COMMANDS = [
  {
    id: 'unix',
    label: 'macOS / Linux',
    shell: 'bash',
    target: 'Terminal',
    command: 'curl -fsSL https://oss.opendeploy.dev/start/install.sh | bash',
  },
  {
    id: 'windows',
    label: 'Windows',
    shell: 'PowerShell',
    target: 'PowerShell',
    command: 'irm https://oss.opendeploy.dev/start/install.ps1 | iex',
  },
] as const

type InstallCommandId = typeof INSTALL_COMMANDS[number]['id']

export function buildAgentLinks(prompt: string) {
  return [
    { id: 'codex',  label: 'Codex',       icon: '/agent-icons/chatgpt.svg', href: `codex://new?${new URLSearchParams({ prompt })}` },
    { id: 'claude', label: 'Claude Code', icon: '/agent-icons/claude.svg',  href: `claude://code/new?${new URLSearchParams({ q: prompt })}` },
    { id: 'cursor', label: 'Cursor',      icon: '/agent-icons/cursor.svg',  href: `https://cursor.com/link/prompt?${new URLSearchParams({ text: prompt })}` },
  ]
}

const AGENT_LAUNCHERS = buildAgentLinks(AGENT_PROMPT)

export default function AgentLauncher({
  copied,
  onCopy,
  className,
  variant = 'full',
}: {
  copied: boolean
  onCopy: (text: string) => void
  className?: string
  variant?: 'full' | 'hero'
}) {
  const [expanded, setExpanded] = useState(false)
  const [agentMenuOpen, setAgentMenuOpen] = useState(true)
  const [installCommandId, setInstallCommandId] = useState<InstallCommandId>('unix')

  const copyActive = () => onCopy(AGENT_PROMPT)
  const toggleAgentMenu = () => setAgentMenuOpen((prev) => !prev)
  const activeInstallCommand =
    INSTALL_COMMANDS.find((command) => command.id === installCommandId) ?? INSTALL_COMMANDS[0]

  if (variant === 'hero') {
    const copyInstallCommand = () => onCopy(activeInstallCommand.command)

    return (
      <aside
        id="start"
        className={`od-agent-launcher od-agent-launcher-hero${className ? ` ${className}` : ''}${copied ? ' is-copied' : ''}`}
        aria-labelledby="od-agent-launcher-title"
      >
        <div className="od-agent-launcher-head">
          <h2 id="od-agent-launcher-title">Install OpenDeploy CLI and agent skill together</h2>
        </div>

        <div className="od-install-switch" role="tablist" aria-label="Choose your install command">
          {INSTALL_COMMANDS.map((installCommand) => (
            <button
              key={installCommand.id}
              type="button"
              className={`od-install-switch-option${installCommand.id === activeInstallCommand.id ? ' is-active' : ''}`}
              role="tab"
              aria-selected={installCommand.id === activeInstallCommand.id}
              onClick={() => setInstallCommandId(installCommand.id)}
            >
              {installCommand.label}
            </button>
          ))}
        </div>

        <button
          type="button"
          className="od-hero-command-preview"
          onClick={copyInstallCommand}
          aria-label={`Copy displayed ${activeInstallCommand.label} OpenDeploy install command`}
        >
          <span className="od-copy-kicker">{activeInstallCommand.shell}</span>
          <code>{activeInstallCommand.command}</code>
        </button>

        <button
          type="button"
          className="od-hero-copy-cta"
          onClick={copyInstallCommand}
          aria-label={`Copy the ${activeInstallCommand.label} OpenDeploy install command`}
        >
          <span>
            {copied
              ? `copied for ${activeInstallCommand.target}`
              : `copy for ${activeInstallCommand.target}`}
          </span>
          <span aria-hidden="true">-&gt;</span>
        </button>
      </aside>
    )
  }

  return (
    <aside
      id="start"
      className={`od-agent-launcher${className ? ` ${className}` : ''}${copied ? ' is-copied' : ''}`}
      aria-labelledby="od-agent-launcher-title"
    >
      <div className="od-agent-launcher-head">
        <span className="od-agent-launcher-kicker">Start opendeploy</span>
        <h2 id="od-agent-launcher-title">Copy one prompt to install OpenDeploy and ship your project</h2>
        <p>
          Paste into your agent. It installs first, then asks before deploying.
        </p>
      </div>

      <button
        type="button"
        className="od-agent-primary"
        onClick={toggleAgentMenu}
        aria-expanded={agentMenuOpen}
      >
        <span className="od-agent-action-icon od-agent-copy-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" focusable="false">
            <rect x="8" y="8" width="11" height="11" rx="2" />
            <path d="M5 15V7a2 2 0 0 1 2-2h8" />
          </svg>
        </span>
        <span className="od-agent-primary-text">
          <span>{copied ? 'Copied prompt' : 'Copy prompt'}</span>
          <span>Choose an agent or copy for any agent.</span>
        </span>
        <span className="od-agent-primary-caret" aria-hidden="true">↓</span>
      </button>

      <div className={`od-agent-menu${agentMenuOpen ? ' is-open' : ''}`} aria-hidden={!agentMenuOpen}>
        <button
          type="button"
          className="od-agent-action od-agent-copy-action"
          onClick={copyActive}
          aria-label="Copy the opendeploy prompt and paste it to your agent"
        >
          <span className="od-agent-action-icon od-agent-copy-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false">
              <rect x="8" y="8" width="11" height="11" rx="2" />
              <path d="M5 15V7a2 2 0 0 1 2-2h8" />
            </svg>
          </span>
          <span>{copied ? 'Copied for any agent' : 'Copy for any agent'}</span>
          <span className="od-agent-action-meta">{copied ? 'done' : 'copy'}</span>
        </button>

        {AGENT_LAUNCHERS.map((agent) => (
          <a
            key={agent.id}
            className={`od-agent-action od-agent-action-${agent.id}`}
            href={agent.href}
            target={agent.href.startsWith('https://') ? '_blank' : undefined}
            rel={agent.href.startsWith('https://') ? 'noopener noreferrer' : undefined}
            onClick={() => onCopy(AGENT_PROMPT)}
            aria-label={`Open ${agent.label} with the opendeploy prompt`}
          >
            <span className="od-agent-action-icon" aria-hidden="true">
              <img src={agent.icon} alt="" decoding="async" />
            </span>
            <span>Open {agent.label}</span>
            <span className="od-agent-arrow" aria-hidden="true">
              <svg viewBox="0 0 24 24" focusable="false">
                <path d="M7 17 17 7" />
                <path d="M9 7h8v8" />
              </svg>
            </span>
          </a>
        ))}
      </div>

      <div
        className={`od-agent-prompt${expanded ? ' is-expanded' : ' is-collapsed'}`}
        aria-label={copied ? 'opendeploy prompt copied' : 'Copy the opendeploy prompt and paste it to your agent'}
        onClick={copyActive}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            copyActive()
          }
        }}
        role="button"
        tabIndex={0}
      >
        <span className="od-copy-kicker">prompt preview — click to copy</span>
        <code>{AGENT_PROMPT}</code>
        <button
          type="button"
          className="od-agent-prompt-toggle"
          aria-expanded={expanded}
          aria-label={expanded ? 'Hide full prompt' : 'Show full prompt'}
          onClick={(event) => {
            event.stopPropagation()
            setExpanded((prev) => !prev)
          }}
        >
          {expanded ? 'Hide' : 'Show full prompt'}
          <span aria-hidden="true">{expanded ? ' ↑' : ' ↓'}</span>
        </button>
      </div>
    </aside>
  )
}
